import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import SearchInput from "../../../components/form/SearchInput";
import FilterByButton from "../../ui/FilterButton";
import DateRangeSelector, { DateRangeType } from "../../ui/DateRangeSelector";
import {
  AdminPublicContractListParams,
  PublicContractPaymentStatus,
  PublicContractStatus,
} from "../../../redux/features/types/publicContractTypes";
import {
  formatYmd,
  formatYmdEndInclusive,
  isPaymentStatus,
  isPublicContractStatus,
  publicContractPaymentOptions,
  publicContractStatusOptions,
} from "./util";

const MANAGED_URL_KEYS = [
  "q",
  "status",
  "payment",
  "dateFrom",
  "dateTo",
  "page",
  "limit",
] as const;

const parseUrlDate = (val: string | null): Date | null => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseFilters = (sp: URLSearchParams) => {
  const status = sp.get("status");
  const payment = sp.get("payment");
  const page = Number(sp.get("page"));
  const limit = Number(sp.get("limit"));

  return {
    search: sp.get("q") ?? "",
    status:
      status && isPublicContractStatus(status)
        ? (status as PublicContractStatus)
        : null,
    paymentStatus:
      payment && isPaymentStatus(payment)
        ? (payment as PublicContractPaymentStatus)
        : null,
    createdRange: {
      startDate: parseUrlDate(sp.get("dateFrom")),
      endDate: parseUrlDate(sp.get("dateTo")),
    } as DateRangeType,
    page: Number.isFinite(page) && page > 0 ? page : 0,
    limit: Number.isFinite(limit) && limit > 0 ? limit : 10,
  };
};

export interface UseInvoiceFiltersReturn {
  inputValue: string;
  setInputValue: (v: string) => void;
  search: string;
  status: PublicContractStatus | null;
  setStatus: (v: PublicContractStatus | null) => void;
  paymentStatus: PublicContractPaymentStatus | null;
  setPaymentStatus: (v: PublicContractPaymentStatus | null) => void;
  createdRange: DateRangeType;
  setCreatedRange: (r: DateRangeType) => void;
  page: number;
  setPage: (v: number) => void;
  limit: number;
  setLimit: (v: number) => void;
  filterResetKey: number;
  bumpFilterResetKey: () => void;
  handleClearFilters: () => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  query: AdminPublicContractListParams;
}

export const useInvoiceFilters = (): UseInvoiceFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initial = useMemo(
    () => parseFilters(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [inputValue, setInputValue] = useState(initial.search);
  const [search, setSearch] = useState(initial.search);
  const [status, setStatusState] = useState<PublicContractStatus | null>(
    initial.status
  );
  const [paymentStatus, setPaymentStatusState] =
    useState<PublicContractPaymentStatus | null>(initial.paymentStatus);
  const [createdRange, setCreatedRangeState] = useState<DateRangeType>(
    initial.createdRange
  );
  const [page, setPage] = useState(initial.page);
  const [limit, setLimit] = useState(initial.limit);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const setStatus = useCallback((v: PublicContractStatus | null) => {
    setStatusState(v);
    setPage(0);
  }, []);

  const setPaymentStatus = useCallback(
    (v: PublicContractPaymentStatus | null) => {
      setPaymentStatusState(v);
      setPage(0);
    },
    []
  );

  const setCreatedRange = useCallback((r: DateRangeType) => {
    setCreatedRangeState(r);
    setPage(0);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(inputValue);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [inputValue]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of MANAGED_URL_KEYS) next.delete(key);

        if (search) next.set("q", search);
        if (status) next.set("status", status);
        if (paymentStatus) next.set("payment", paymentStatus);
        if (createdRange.startDate) {
          next.set("dateFrom", formatYmd(createdRange.startDate));
        }
        if (createdRange.endDate) {
          next.set("dateTo", formatYmd(createdRange.endDate));
        }
        if (page > 0) next.set("page", String(page));
        if (limit !== 10) next.set("limit", String(limit));
        return next;
      },
      { replace: true }
    );
  }, [
    search,
    status,
    paymentStatus,
    createdRange,
    page,
    limit,
    setSearchParams,
  ]);

  const bumpFilterResetKey = useCallback(() => {
    setFilterResetKey((k) => k + 1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setInputValue("");
    setSearch("");
    setStatusState(null);
    setPaymentStatusState(null);
    setCreatedRangeState({ startDate: null, endDate: null });
    setFilterResetKey((k) => k + 1);
    setPage(0);
  }, []);

  const isCreatedRangeActive = !!(
    createdRange.startDate || createdRange.endDate
  );
  const activeFilterCount =
    (search ? 1 : 0) +
    (status ? 1 : 0) +
    (paymentStatus ? 1 : 0) +
    (isCreatedRangeActive ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const query = useMemo<AdminPublicContractListParams>(
    () => ({
      page: page + 1,
      limit,
      status: status || undefined,
      paymentStatus: paymentStatus || undefined,
      search: search || undefined,
      dateFrom: formatYmd(createdRange.startDate) || undefined,
      dateTo: formatYmdEndInclusive(createdRange.endDate) || undefined,
    }),
    [page, limit, status, paymentStatus, search, createdRange]
  );

  return {
    inputValue,
    setInputValue,
    search,
    status,
    setStatus,
    paymentStatus,
    setPaymentStatus,
    createdRange,
    setCreatedRange,
    page,
    setPage,
    limit,
    setLimit,
    filterResetKey,
    bumpFilterResetKey,
    handleClearFilters,
    activeFilterCount,
    hasActiveFilters,
    query,
  };
};

const FilterField: FC<{
  label: string;
  htmlFor: string;
  active?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ label, htmlFor, active = false, children, className = "" }) => (
  <div className={`flex flex-col gap-1 w-full ${className}`}>
    <label
      htmlFor={htmlFor}
      className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide"
    >
      {label}
    </label>
    <div className={`rounded-lg ${active ? "ring-2 ring-primary/40" : ""}`}>
      {children}
    </div>
  </div>
);

interface InvoiceFilterBarProps {
  filters: UseInvoiceFiltersReturn;
  panelId?: string;
}

/** Coupon-style collapsible filters for invoice list. */
export const InvoiceFilterBar: FC<InvoiceFilterBarProps> = ({
  filters,
  panelId = "invoices-filter-panel",
}) => {
  const [filtersOpen, setFiltersOpen] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 640px)").matches
  );

  const {
    inputValue,
    setInputValue,
    search,
    status,
    setStatus,
    paymentStatus,
    setPaymentStatus,
    createdRange,
    setCreatedRange,
    filterResetKey,
    handleClearFilters,
    activeFilterCount,
    hasActiveFilters,
  } = filters;

  const isCreatedRangeActive = !!(
    createdRange.startDate || createdRange.endDate
  );

  const searchId = "invoice-filter-search";
  const statusId = "invoice-filter-status";
  const paymentId = "invoice-filter-payment";
  const createdId = "invoice-filter-created";

  return (
    <div className="mb-4">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
          aria-expanded={filtersOpen}
          aria-controls={panelId}
        >
          <SlidersHorizontal className="w-4 h-4 text-gray-500" />
          <span>Filters</span>
          {hasActiveFilters && (
            <span className="bg-lightPurple text-accent text-[10px] font-semibold px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
              filtersOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 hover:underline"
          >
            <X className="w-3.5 h-3.5" />
            Clear filters
          </button>
        )}
      </div>

      <div id={panelId} className={`mt-4 ${filtersOpen ? "" : "hidden"}`}>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <FilterField
            label="Search"
            htmlFor={searchId}
            active={!!search}
            className="w-full sm:w-[260px]"
          >
            <SearchInput
              id={searchId}
              name="invoiceSearch"
              autoComplete="off"
              wrapperClassName=""
              placeholder="Code, title, buyer, or vendor"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </FilterField>

          <FilterField
            label="Invoice status"
            htmlFor={statusId}
            active={!!status}
            className="w-full sm:w-[220px]"
          >
            <FilterByButton
              key={`status-${filterResetKey}`}
              id={statusId}
              name="invoiceStatus"
              fullWidth
              defaultValue={status}
              buttonLabel="All statuses"
              options={publicContractStatusOptions}
              onSelect={(value: PublicContractStatus | null) =>
                setStatus(value)
              }
            />
          </FilterField>

          <FilterField
            label="Payment status"
            htmlFor={paymentId}
            active={!!paymentStatus}
            className="w-full sm:w-[220px]"
          >
            <FilterByButton
              key={`payment-${filterResetKey}`}
              id={paymentId}
              name="invoicePaymentStatus"
              fullWidth
              defaultValue={paymentStatus}
              buttonLabel="All payment statuses"
              options={publicContractPaymentOptions}
              onSelect={(value: PublicContractPaymentStatus | null) =>
                setPaymentStatus(value)
              }
            />
          </FilterField>

          <FilterField
            label="Created date"
            htmlFor={createdId}
            active={isCreatedRangeActive}
            className="w-full sm:w-[260px]"
          >
            <DateRangeSelector
              key={`created-${filterResetKey}`}
              id={createdId}
              name="invoiceCreatedRange"
              disableFuture
              className="w-full"
              placeholder="Created from – to"
              initialRange={createdRange}
              onChange={(range) => setCreatedRange(range)}
            />
          </FilterField>
        </div>
      </div>
    </div>
  );
};
